/**
 * Hnefatafl（维京板棋）游戏组件
 * 实现游戏逻辑、规则判定和AI对战
 */

import React, { useState, useEffect, useCallback } from 'react';
import { GameMode, Difficulty } from '../types/game';
import {
  HnefataflGameState,
  HnefataflPiece,
  HnefataflPieceType,
  HnefataflMove,
  Position,
  HNEFATAFL_CONFIG
} from '../types/game';
import HnefataflBoard from './HnefataflBoard';
import VictoryEffect from './VictoryEffect';
import GameShareButton from './GameShareButton';

interface HnefataflGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
}

const HnefataflGame: React.FC<HnefataflGameProps> = ({
  gameMode,
  difficulty = 'medium',
  onBackToMenu,
  onBackToModeSelect
}) => {
  // 创建初始棋盘
  const createInitialBoard = (): (HnefataflPiece | null)[][] => {
    const board: (HnefataflPiece | null)[][] = Array(11).fill(null).map(() => Array(11).fill(null));

    // 放置防守方棋子（国王和兵卒）
    // 国王在中心位置 (5, 5)
    board[5][5] = { type: 'king', player: 'defender' };

    // 防守方兵卒围绕国王布置
    // 上下左右的兵卒
    board[5][4] = { type: 'warrior', player: 'defender' };
    board[5][6] = { type: 'warrior', player: 'defender' };
    board[4][5] = { type: 'warrior', player: 'defender' };
    board[6][5] = { type: 'warrior', player: 'defender' };

    // 对角线上的兵卒
    board[4][4] = { type: 'warrior', player: 'defender' };
    board[4][6] = { type: 'warrior', player: 'defender' };
    board[6][4] = { type: 'warrior', player: 'defender' };
    board[6][6] = { type: 'warrior', player: 'defender' };

    // 外圈防守方兵卒
    board[3][5] = { type: 'warrior', player: 'defender' };
    board[5][3] = { type: 'warrior', player: 'defender' };
    board[7][5] = { type: 'warrior', player: 'defender' };
    board[5][7] = { type: 'warrior', player: 'defender' };

    // 放置进攻方棋子（24枚），按标准维京板棋布局
    // 上边缘：6枚棋子
    
    board[0][3] = { type: 'taflman', player: 'attacker' };
    board[0][4] = { type: 'taflman', player: 'attacker' };
    board[0][5] = { type: 'taflman', player: 'attacker' };
    board[0][6] = { type: 'taflman', player: 'attacker' };
    board[0][7] = { type: 'taflman', player: 'attacker' };
    board[1][5] = { type: 'taflman', player: 'attacker' };

    // 下边缘：6枚棋子
    board[9][5] = { type: 'taflman', player: 'attacker' };
    board[10][3] = { type: 'taflman', player: 'attacker' };
    board[10][4] = { type: 'taflman', player: 'attacker' };
    board[10][5] = { type: 'taflman', player: 'attacker' };
    board[10][6] = { type: 'taflman', player: 'attacker' };
    board[10][7] = { type: 'taflman', player: 'attacker' };
    

    // 左边缘：6枚棋子
    board[3][0] = { type: 'taflman', player: 'attacker' };
    board[4][0] = { type: 'taflman', player: 'attacker' };
    board[5][0] = { type: 'taflman', player: 'attacker' };
    board[6][0] = { type: 'taflman', player: 'attacker' };
    board[7][0] = { type: 'taflman', player: 'attacker' };
    board[5][1] = { type: 'taflman', player: 'attacker' };

    // 右边缘：6枚棋子
    board[3][10] = { type: 'taflman', player: 'attacker' };
    board[4][10] = { type: 'taflman', player: 'attacker' };
    board[5][10] = { type: 'taflman', player: 'attacker' };
    board[6][10] = { type: 'taflman', player: 'attacker' };
    board[7][10] = { type: 'taflman', player: 'attacker' };
    board[5][9] = { type: 'taflman', player: 'attacker' };

    return board;
  };

  // 创建初始游戏状态
  const createInitialGameState = (): HnefataflGameState => {
    const board = createInitialBoard();
    return {
      board,
      currentPlayer: 'attacker', // 进攻方先行
      status: 'playing',
      winner: null,
      moves: [],
      gameHistory: [],
      gameMode,
      gameType: 'hnefatafl',
      difficulty,
      isAIThinking: false,
      selectedPiece: undefined,
      possibleMoves: [],
      capturedPieces: { attacker: [], defender: [] },
      kingPosition: { row: 5, col: 5 },
      corners: [
        { row: 0, col: 0 },
        { row: 0, col: 10 },
        { row: 10, col: 0 },
        { row: 10, col: 10 }
      ],
      totalMoves: 0,
      captureMoves: 0,
      cornerEscapeAttempts: 0,
      forbiddenPositions: [
        { row: 5, col: 5 }, // 中心
        { row: 0, col: 0 }, // 四角
        { row: 0, col: 10 },
        { row: 10, col: 0 },
        { row: 10, col: 10 }
      ],
      kingCaptured: false,
      kingEscaped: false
    };
  };

  const [gameState, setGameState] = useState<HnefataflGameState>(createInitialGameState);
  const [gameMessage, setGameMessage] = useState<string>('');
  const [showVictoryEffect, setShowVictoryEffect] = useState<boolean>(false);
  const [lastWinner, setLastWinner] = useState<'attacker' | 'defender' | null>(null);
  const [winRecord, setWinRecord] = useState<{ attacker: number; defender: number }>({ attacker: 0, defender: 0 });
  const [isBoardEnlarged, setIsBoardEnlarged] = useState<boolean>(false);
  const [showRules, setShowRules] = useState<boolean>(false);
  const [canUndo, setCanUndo] = useState(false);

  // 判断是否为禁止位置
  const isForbiddenPosition = (row: number, col: number): boolean => {
    return gameState.forbiddenPositions.some(pos => pos.row === row && pos.col === col);
  };

  // 判断是否为角落位置
  const isCorner = (row: number, col: number): boolean => {
    return gameState.corners.some(corner => corner.row === row && corner.col === col);
  };

  // 检查国王是否被围堵
  const isKingSurrounded = (kingPos: Position, board: (HnefataflPiece | null)[][]): boolean => {
    const directions = [
      { dr: -1, dc: 0 }, // 上
      { dr: 1, dc: 0 },  // 下
      { dr: 0, dc: -1 }, // 左
      { dr: 0, dc: 1 }   // 右
    ];

    let surroundedCount = 0;
    for (const { dr, dc } of directions) {
      const adjRow = kingPos.row + dr;
      const adjCol = kingPos.col + dc;

      if (adjRow >= 0 && adjRow < 11 && adjCol >= 0 && adjCol < 11) {
        const adjPiece = board[adjRow][adjCol];
        // 如果相邻位置是进攻方棋子或者是边界（角落/边缘）
        if (adjPiece && adjPiece.player === 'attacker') {
          surroundedCount++;
        }
      }
    }

    // 国王需要四面都被进攻方棋子围住才算被围堵
    return surroundedCount === 4;
  };

  // 检查国王是否在角落被特殊夹击（2个攻击者 + 1个角落）
  const isKingTrappedAtCorner = (kingPos: Position, board: (HnefataflPiece | null)[][]): boolean => {
    // 首先检查国王是否在角落旁边（距离为1）
    const corners = [
      { row: 0, col: 0 },
      { row: 0, col: 10 },
      { row: 10, col: 0 },
      { row: 10, col: 10 }
    ];

    // 检查国王是否在某个角落的相邻位置
    const adjacentCorner = corners.find(corner =>
      Math.abs(kingPos.row - corner.row) <= 1 &&
      Math.abs(kingPos.col - corner.col) <= 1
    );

    if (!adjacentCorner) {
      return false; // 国王不在任何角落的相邻位置
    }

    // 检查国王是否被夹在角落和攻击者之间
    const directions = [{ dr: -1, dc: 0 }, { dr: 1, dc: 0 }, { dr: 0, dc: -1 }, { dr: 0, dc: 1 }];
    let attackerCount = 0;
    let hasCornerSupport = false;

    for (const { dr, dc } of directions) {
      const newRow = kingPos.row + dr;
      const newCol = kingPos.col + dc;

      // 检查这个方向是否是角落
      if (isCorner(newRow, newCol)) {
        hasCornerSupport = true;
        continue;
      }

      // 检查边界外
      if (newRow < 0 || newRow >= 11 || newCol < 0 || newCol >= 11) {
        continue;
      }

      const adjacentCell = board[newRow][newCol];
      if (adjacentCell && adjacentCell.type === 'attacker') {
        attackerCount++;
      }
    }

    // 特殊胜利条件：国王被2个攻击者和1个角落同时围住
    // 这种情况下，国王只差一步就能逃脱到角落获胜，但同时被围死
    const isTrapped = attackerCount === 2 && hasCornerSupport;

    if (isTrapped) {
      console.log('=== 检测到特殊夹击情况 ===', {
        kingPosition: kingPos,
        adjacentCorner,
        attackerCount,
        hasCornerSupport,
        surroundings: directions.map(({ dr, dc }) => {
          const newRow = kingPos.row + dr;
          const newCol = kingPos.col + dc;
          const piece = board[newRow]?.[newCol];
          return {
            direction: `${dr > 0 ? '下' : dr < 0 ? '上' : ''}${dc > 0 ? '右' : dc < 0 ? '左' : ''}`,
            position: { row: newRow, col: newCol },
            piece: piece?.type || 'empty',
            isCorner: isCorner(newRow, newCol)
          };
        })
      });
    }

    return isTrapped;
  };

  // 获取被夹吃的棋子（重写夹吃逻辑）
  const getCapturedPieces = useCallback((from: Position, to: Position, board: (HnefataflPiece | null)[][]): { pieces: HnefataflPiece[], positions: Position[] } => {
    const movingPiece = board[from.row][from.col];
    if (!movingPiece) return { pieces: [], positions: [] };

    const captured: HnefataflPiece[] = [];
    const capturedPositions: Position[] = [];
    const directions = [
      { dr: -1, dc: 0 }, // 上
      { dr: 1, dc: 0 },  // 下
      { dr: 0, dc: -1 }, // 左
      { dr: 0, dc: 1 }   // 右
    ];

    // console.log('=== 检查夹吃 ===', { from, to, movingPiece });

    // 夹吃规则：移动到目标位置后，检查四个方向上是否有敌方棋子被夹在移动棋子和另一个己方棋子之间
    for (const { dr, dc } of directions) {
      // 检查移动棋子的一侧是否有敌方棋子
      const enemyRow = to.row + dr;
      const enemyCol = to.col + dc;

      // 边界检查
      if (enemyRow < 0 || enemyRow >= 11 || enemyCol < 0 || enemyCol >= 11) continue;

      const enemyPiece = board[enemyRow][enemyCol];
      if (!enemyPiece || enemyPiece.player === movingPiece.player) {
        continue; // 没有敌方棋子或是我方棋子
      }

      // 检查敌方棋子的另一侧是否有支撑棋子
      const supportRow = enemyRow + dr;
      const supportCol = enemyCol + dc;

      let hasSupport = false;

      // 优先检查特殊规则：角落永远作为夹吃支撑点（适用于任何敌方棋子）
      // 维京板棋规则：角落永远相当于走棋一方的一枚虚拟棋子
      if (isCorner(supportRow, supportCol)) {
        hasSupport = true;
        console.log('=== 角落作为夹吃支撑点 ===', {
          capturedPiece: enemyPiece,
          supportRow,
          supportCol,
          capturedByCorner: true
        });
      }
      // 特殊规则：王座作为支撑（但只有当王座为空且被夹的是国王时）
      // 注意：根据一些维京板棋变体规则，空的王座可以作为支撑来夹国王
      else if (!hasSupport && enemyPiece.type === 'king' && supportRow === 5 && supportCol === 5) {
        const thronePiece = board[5][5];
        if (!thronePiece) { // 王座是空的
          hasSupport = true;
          console.log('=== 空王座作为国王支撑 ===');
        }
      }
      // 最后检查是否在棋盘内有己方棋子作为支撑
      else if (supportRow >= 0 && supportRow < 11 && supportCol >= 0 && supportCol < 11) {
        const supportPiece = board[supportRow][supportCol];
        if (supportPiece && supportPiece.player === movingPiece.player) {
          hasSupport = true;
          console.log('=== 找到己方棋子支撑 ===', {
            enemyPos: { row: enemyRow, col: enemyCol },
            supportPos: { row: supportRow, col: supportCol },
            supportPiece
          });
        }
      }

      if (hasSupport) {
        // 存储被吃棋子和它的位置
        captured.push(enemyPiece);
        capturedPositions.push({ row: enemyRow, col: enemyCol });

        // console.log('=== 成功夹吃 ===', {
        //   captured: enemyPiece,
        //   capturedPosition: { row: enemyRow, col: enemyCol },
        //   from: to,
        //   support: { row: supportRow, col: supportCol }
        // });
      }
    }

    // 特殊检查：如果被夹的棋子中已经包含国王，则不需要再检查围困
    const hasKingCaptured = captured.some(piece => piece.type === 'king');

    // 只有在没有通过夹吃捕获国王的情况下，才检查国王是否被围困
    if (!hasKingCaptured) {
      // 内联查找国王位置以避免循环依赖
      for (let row = 0; row < 11; row++) {
        for (let col = 0; col < 11; col++) {
          const piece = board[row][col];
          if (piece && piece.type === 'king') {
            if (isKingSurrounded({ row, col }, board)) {
              captured.push(piece);
              console.log('=== 国王被围困捕获 ===', { row, col });
            }
            break; // 找到国王后就跳出内层循环
          }
        }
        // 找到国王后跳出外层循环
        if (captured.some(piece => piece.type === 'king')) {
          break;
        }
      }
    }

    // console.log('=== 夹吃检查完成 ===', { totalCaptured: captured.length, captured, capturedPositions });
    return { pieces: captured, positions: capturedPositions };
  }, [isCorner, isKingSurrounded]);

  // 获取棋子可能的移动（优化性能）
  const getPossibleMoves = useCallback((position: Position, board: (HnefataflPiece | null)[][]): HnefataflMove[] => {
    const piece = board[position.row][position.col];
    if (!piece) return [];

    const moves: HnefataflMove[] = [];
    const { row, col } = position;
    const isKing = piece.type === 'king';

    // 预定义方向，避免重复创建
    const directions = [
      { dr: -1, dc: 0 }, // 上
      { dr: 1, dc: 0 },  // 下
      { dr: 0, dc: -1 }, // 左
      { dr: 0, dc: 1 }   // 右
    ];

    for (const { dr, dc } of directions) {
      let newRow = row + dr;
      let newCol = col + dc;

      // 沿着该方向移动直到遇到障碍或边界
      while (newRow >= 0 && newRow < 11 && newCol >= 0 && newCol < 11) {
        const targetCell = board[newRow][newCol];

        // 如果目标位置有棋子，停止这个方向的移动
        if (targetCell) {
          break;
        }

        // 检查是否可以移动到这个位置
        const isTargetForbidden = isForbiddenPosition(newRow, newCol);
        const isTargetCorner = isCorner(newRow, newCol);

        if (isTargetForbidden) {
          // 只有国王可以移动到角落
          if (isKing && isTargetCorner) {
            const move: HnefataflMove = {
              player: piece.player,
              from: position,
              to: { row: newRow, col: newCol },
              isCapture: false,
              isCornerEscape: true
            };
            moves.push(move);
          }
          // 其他情况不能移动到禁止位置
          break;
        }

        // 检查这个移动是否会吃子
        const captureResult = getCapturedPieces(position, { row: newRow, col: newCol }, board);
        const move: HnefataflMove = {
          player: piece.player,
          from: position,
          to: { row: newRow, col: newCol },
          captures: captureResult.pieces.length > 0 ? captureResult.pieces : undefined,
          capturedPositions: captureResult.pieces.length > 0 ? captureResult.positions : undefined,
          isCapture: captureResult.pieces.length > 0
        };
        moves.push(move);

        newRow += dr;
        newCol += dc;
      }
    }

    return moves;
  }, [isForbiddenPosition, isCorner, getCapturedPieces]);

  // 查找国王位置
  const findKingPosition = (board: (HnefataflPiece | null)[][]): Position | null => {
    for (let row = 0; row < 11; row++) {
      for (let col = 0; col < 11; col++) {
        const piece = board[row][col];
        if (piece && piece.type === 'king') {
          return { row, col };
        }
      }
    }
    return null;
  };

  // 执行移动
  const makeMove = useCallback((move: HnefataflMove): HnefataflGameState => {
    const newBoard = gameState.board.map(row => [...row]);
    const movingPiece = newBoard[move.from.row][move.from.col];

    if (!movingPiece) return gameState;

    // 移动棋子
    newBoard[move.to.row][move.to.col] = movingPiece;
    newBoard[move.from.row][move.from.col] = null;

    // 处理吃子（直接使用move中已经计算好的captures和位置）
    const capturedPieces = { ...gameState.capturedPieces };
    if (move.captures && move.captures.length > 0) {
      // console.log('=== 开始处理吃子 ===', {
      //   move: move,
      //   captures: move.captures,
      //   capturedPositions: move.capturedPositions
      // });

      // 使用存储的位置信息直接移除被吃的棋子
      move.captures.forEach((capturedPiece, index) => {
        if (move.capturedPositions && move.capturedPositions[index]) {
          const capturedPos = move.capturedPositions[index];

          // 验证该位置的棋子确实匹配
          const piece = newBoard[capturedPos.row][capturedPos.col];
          if (piece &&
              piece.type === capturedPiece.type &&
              piece.player === capturedPiece.player) {

            // 移除被吃的棋子
            newBoard[capturedPos.row][capturedPos.col] = null;

            // 添加到被吃棋子列表
            if (capturedPiece.player === 'attacker') {
              capturedPieces.attacker.push(capturedPiece);
            } else {
              capturedPieces.defender.push(capturedPiece);
            }

            console.log('=== 移除被吃棋子 ===', {
              capturedPiece,
              capturedPosition: capturedPos,
              success: true
            });
          } else {
            console.error('=== 被吃棋子位置不匹配 ===', {
              capturedPiece,
              capturedPosition: capturedPos,
              actualPiece: piece
            });
          }
        } else {
          console.error('=== 缺少被吃棋子位置信息 ===', {
            capturedPiece,
            index,
            capturedPositions: move.capturedPositions,
            moveComplete: move
          });

          // 回退方案：重新计算被吃棋子的位置
          console.log('=== 使用回退方案重新计算位置 ===');
          const directions = [
            { dr: -1, dc: 0 }, // 上
            { dr: 1, dc: 0 },  // 下
            { dr: 0, dc: -1 }, // 左
            { dr: 0, dc: 1 }   // 右
          ];

          let found = false;
          for (const { dr, dc } of directions) {
            const checkRow = move.to.row + dr;
            const checkCol = move.to.col + dc;

            if (checkRow >= 0 && checkRow < 11 && checkCol >= 0 && checkCol < 11) {
              const piece = newBoard[checkRow][checkCol];
              if (piece &&
                  piece.type === capturedPiece.type &&
                  piece.player === capturedPiece.player) {
                // 找到匹配的被吃棋子，移除它
                newBoard[checkRow][checkCol] = null;
                found = true;

                // 添加到被吃棋子列表
                if (capturedPiece.player === 'attacker') {
                  capturedPieces.attacker.push(capturedPiece);
                } else {
                  capturedPieces.defender.push(capturedPiece);
                }

                console.log('=== 回退方案成功移除棋子 ===', {
                  capturedPiece,
                  foundPosition: { row: checkRow, col: checkCol },
                  success: true
                });
                break;
              }
            }
          }

          if (!found) {
            console.error('=== 回退方案也未能找到被吃棋子 ===', {
              capturedPiece,
              move: move
            });
          }
        }
      });
    }

    // 更新国王位置
    let kingPosition = gameState.kingPosition;
    if (movingPiece.type === 'king') {
      kingPosition = move.to;
    }

    // 检查胜负条件
    let winner: 'attacker' | 'defender' | null = null;
    let kingCaptured = false;
    let kingEscaped = false;

    // 检查国王是否逃脱到角落（防守方获胜）
    if (movingPiece.type === 'king' && isCorner(move.to.row, move.to.col)) {
      winner = 'defender';
      kingEscaped = true;
    }

    // 检查国王是否被围堵或被吃（进攻方获胜）
    const kingPiece = newBoard[kingPosition.row][kingPosition.col];
    if (!kingPiece || kingPiece.type !== 'king') {
      winner = 'attacker';
      kingCaptured = true;
    } else if (isKingSurrounded(kingPosition, newBoard)) {
      winner = 'attacker';
      kingCaptured = true;
    } else if (isKingTrappedAtCorner(kingPosition, newBoard)) {
      // 特殊情况：国王被2个攻击者和角落同时围住
      winner = 'attacker';
      kingCaptured = true;
      console.log('=== 国王被夹在角落，攻击方获胜 ===', { kingPosition });
    }

    const newGameState: HnefataflGameState = {
      ...gameState,
      board: newBoard,
      currentPlayer: gameState.currentPlayer === 'attacker' ? 'defender' : 'attacker',
      selectedPiece: undefined,
      possibleMoves: [],
      moves: [...gameState.moves, move],
      gameHistory: [...gameState.gameHistory, gameState],
      capturedPieces,
      kingPosition,
      totalMoves: gameState.totalMoves + 1,
      captureMoves: move.captures ? gameState.captureMoves + 1 : gameState.captureMoves,
      cornerEscapeAttempts: move.isCornerEscape ? gameState.cornerEscapeAttempts + 1 : gameState.cornerEscapeAttempts,
      status: winner ? 'finished' : 'playing',
      winner,
      kingCaptured,
      kingEscaped
    };

    return newGameState;
  }, [gameState, isCorner, isKingSurrounded]);

  // 处理格子点击（优化性能和用户体验）
  const handleCellClick = useCallback((position: Position) => {
    // 防止重复点击和无效状态
    if (gameState.status === 'finished' || gameState.isAIThinking) return;

    const piece = gameState.board[position.row][position.col];

    // 如果点击了当前玩家的棋子
    if (piece && piece.player === gameState.currentPlayer) {
      // 避免重复选择同一个棋子
      if (gameState.selectedPiece?.row === position.row && gameState.selectedPiece?.col === position.col) {
        return;
      }

      const moves = getPossibleMoves(position, gameState.board);

      // 批量状态更新，减少重渲染
      setGameState(prev => ({
        ...prev,
        selectedPiece: position,
        possibleMoves: moves
      }));
    }
    // 如果已选中棋子，尝试移动
    else if (gameState.selectedPiece) {
      const move = gameState.possibleMoves.find(
        m => m.to.row === position.row && m.to.col === position.col
      );

      if (move) {
        // 使用 setTimeout 确保UI更新完成后再执行移动
        setTimeout(() => {
          const newState = makeMove(move);
          setGameState(newState);
        }, 0);
      } else {
        // 取消选择
        setGameState(prev => ({
          ...prev,
          selectedPiece: undefined,
          possibleMoves: []
        }));
      }
    }
  }, [gameState.currentPlayer, gameState.selectedPiece, gameState.status, gameState.isAIThinking, gameState.board, getPossibleMoves, makeMove]);

  // 重置游戏
  const resetGame = useCallback(() => {
    setGameState(createInitialGameState());
    setGameMessage('游戏重新开始！');
    setLastWinner(null);
    setShowVictoryEffect(false);
    setTimeout(() => setGameMessage(''), 2000);
  }, []);

  // 悔棋
  const undoMove = useCallback(() => {
    if (gameState.gameHistory.length > 0) {
      const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
      setGameState(previousState);
      setGameMessage('悔棋成功');
      setTimeout(() => setGameMessage(''), 1500);
    }
  }, [gameState.gameHistory]);

  // AI智能策略函数
  const getBestMove = (moves: HnefataflMove[], board: (HnefataflPiece | null)[][]): HnefataflMove => {
    if (moves.length === 0) return moves[0];

    // 按优先级分类移动
    const captureMoves: HnefataflMove[] = [];
    const kingThreateningMoves: HnefataflMove[] = [];
    const strategicMoves: HnefataflMove[] = [];
    const normalMoves: HnefataflMove[] = [];

    // 国王位置
    let kingPos: Position | null = null;
    for (let row = 0; row < 11; row++) {
      for (let col = 0; col < 11; col++) {
        const piece = board[row][col];
        if (piece && piece.type === 'king') {
          kingPos = { row, col };
          break;
        }
      }
      if (kingPos) break;
    }

    // 评估每个移动
    moves.forEach(move => {
      const score = evaluateMove(move, board, kingPos);

      if (move.isCapture && move.captures && move.captures.length > 0) {
        captureMoves.push({ ...move, score });
      } else if (score >= 50) {
        kingThreateningMoves.push({ ...move, score });
      } else if (score >= 20) {
        strategicMoves.push({ ...move, score });
      } else {
        normalMoves.push({ ...move, score });
      }
    });

    // 按分数排序
    [captureMoves, kingThreateningMoves, strategicMoves, normalMoves].forEach(arr => {
      arr.sort((a, b) => (b.score || 0) - (a.score || 0));
    });

    console.log('AI策略分析:', {
      吃子移动: captureMoves.length,
      威胁国王: kingThreateningMoves.length,
      战略移动: strategicMoves.length,
      普通移动: normalMoves.length
    });

    // 按优先级选择
    if (captureMoves.length > 0) {
      //console.log('AI策略: 优先吃子，选择最高分移动');
      return captureMoves[0];
    }

    if (kingThreateningMoves.length > 0) {
      //console.log('AI策略: 威胁国王');
      return kingThreateningMoves[0];
    }

    if (strategicMoves.length > 0) {
      //console.log('AI策略: 战略位置');
      return strategicMoves[0];
    }

    //console.log('AI策略: 普通移动');
    return normalMoves[0];
  };

  // 评估移动分数
  const evaluateMove = (move: HnefataflMove, board: (HnefataflPiece | null)[][], kingPos: Position | null): number => {
    let score = 10; // 基础分数

    // 吃子加分
    if (move.isCapture && move.captures) {
      move.captures.forEach(captured => {
        if (captured.type === 'king') {
          score += 1000; // 吃掉国王直接获胜
        } else if (captured.type === 'warrior') {
          score += 100; // 吃掉防守方兵卒
        } else {
          score += 50; // 吃掉普通防守方
        }
      });
    }

    // 威胁国王加分
    if (kingPos) {
      const distanceToKing = Math.abs(move.to.row - kingPos.row) + Math.abs(move.to.col - kingPos.col);
      if (distanceToKing <= 2) {
        score += (3 - distanceToKing) * 30; // 越近国王分数越高
      }
    }

    // 占据战略位置加分
    const strategicPositions = [
      { row: 2, col: 2 }, { row: 2, col: 5 }, { row: 2, col: 8 },
      { row: 5, col: 2 }, { row: 5, col: 8 },
      { row: 8, col: 2 }, { row: 8, col: 5 }, { row: 8, col: 8 }
    ];

    strategicPositions.forEach(pos => {
      if (move.to.row === pos.row && move.to.col === pos.col) {
        score += 25; // 战略位置加分
      }
    });

    // 接近角落加分（限制国王逃跑路线）
    const corners = [{row: 0, col: 0}, {row: 0, col: 10}, {row: 10, col: 0}, {row: 10, col: 10}];
    corners.forEach(corner => {
      const distance = Math.abs(move.to.row - corner.row) + Math.abs(move.to.col - corner.col);
      if (distance <= 2) {
        score += 15; // 接近角落加分
      }
    });

    // 中心控制加分
    const centerDistance = Math.abs(move.to.row - 5) + Math.abs(move.to.col - 5);
    if (centerDistance <= 3) {
      score += (4 - centerDistance) * 5; // 越接近中心分数越高
    }

    return score;
  };

  // 获取移动策略描述
  const getMoveStrategy = (move: HnefataflMove): string => {
    if (move.isCapture && move.captures && move.captures.length > 0) {
      const capturedTypes = move.captures.map(c =>
        c.type === 'king' ? '国王' :
        c.type === 'warrior' ? '兵卒' : '棋子'
      ).join('、');
      return `吃子(${capturedTypes})`;
    }

    const { row, col } = move.to;

    // 检查是否接近角落
    const corners = [{row: 0, col: 0}, {row: 0, col: 10}, {row: 10, col: 0}, {row: 10, col: 10}];
    const nearCorner = corners.some(corner =>
      Math.abs(row - corner.row) + Math.abs(col - corner.col) <= 2
    );
    if (nearCorner) return '封锁角落';

    // 检查是否接近中心
    const centerDistance = Math.abs(row - 5) + Math.abs(col - 5);
    if (centerDistance <= 2) return '控制中心';

    // 检查是否在战略位置
    const strategicPositions = [
      {row: 2, col: 2}, {row: 2, col: 5}, {row: 2, col: 8},
      {row: 5, col: 2}, {row: 5, col: 8},
      {row: 8, col: 2}, {row: 8, col: 5}, {row: 8, col: 8}
    ];
    const isStrategic = strategicPositions.some(pos => row === pos.row && col === pos.col);
    if (isStrategic) return '占据要点';

    return '战略移动';
  };

  // 简单AI逻辑 - 完全重写避免状态冲突
  useEffect(() => {
    /*console.log('AI useEffect触发:', {
      gameMode,
      currentPlayer: gameState.currentPlayer,
      status: gameState.status,
      isAIThinking: gameState.isAIThinking
    });
    */

    if (gameMode === 'pvc' && gameState.currentPlayer === 'attacker' && gameState.status === 'playing' && !gameState.isAIThinking) {
      //console.log('AI开始思考...');

      setGameMessage('AI正在思考...');

      // 获取当前函数的引用，避免闭包问题
      const currentGetPossibleMoves = getPossibleMoves;
      const currentMakeMove = makeMove;
      const currentSetGameState = setGameState;
      const currentSetGameMessage = setGameMessage;

      // 使用较短的时间进行调试
      const timer = setTimeout(() => {
        //console.log('AI定时器回调执行...');

        // 首先设置AI思考状态，防止重复触发
        currentSetGameState(prev => ({ ...prev, isAIThinking: true }));

        // 然后执行AI移动逻辑
        setTimeout(() => {
          currentSetGameState((prevState) => {
            //console.log('执行AI移动逻辑，当前状态:', prevState);

            try {
              // 获取当前棋盘状态
              const currentBoard = prevState.board;
              //console.log('当前棋盘状态:', currentBoard);

              // 测试：手动检查位置(0,3)的棋子是否能移动
              const testPiece = currentBoard[0][3];
              //console.log('测试棋子 (0,3):', testPiece);
              if (testPiece && testPiece.player === 'attacker') {
                //console.log('测试位置(0,3)的移动计算...');
                try {
                  const testMoves = currentGetPossibleMoves({ row: 0, col: 3 }, currentBoard);
                  //console.log('位置(0,3)的可能移动:', testMoves);
                } catch (error) {
                  //console.error('测试移动计算时出错:', error);
                }
              }

              // 获取所有进攻方棋子的可能移动
              const allMoves: HnefataflMove[] = [];
              //console.log('开始计算移动...');

              for (let row = 0; row < 11; row++) {
                for (let col = 0; col < 11; col++) {
                  const piece = currentBoard[row][col];
                  if (piece && piece.player === 'attacker') {
                    //console.log(`检查棋子位置 (${row}, ${col})，类型: ${piece.type}`);

                    try {
                      const moves = currentGetPossibleMoves({ row, col }, currentBoard);
                      //console.log(`该位置找到的移动:`, moves);
                      if (moves && moves.length > 0) {
                        allMoves.push(...moves);
                        //console.log(`该位置有 ${moves.length} 个可能移动:`, moves.map(m => `(${m.to.row},${m.to.col})`));
                      }
                    } catch (error) {
                      //console.error(`计算位置 (${row}, ${col}) 的移动时出错:`, error);
                    }
                  }
                }
              }

              //console.log(`总共找到 ${allMoves.length} 个可能移动`);

              if (allMoves.length > 0) {
                // 智能AI策略 - 优先选择吃子移动
                const selectedMove = getBestMove(allMoves, currentBoard);
                //console.log('AI选择移动:', selectedMove, '策略:', getMoveStrategy(selectedMove));

                currentSetGameMessage('AI移动棋子');

                // 执行移动
                //console.log('准备执行移动...');
                const newState = currentMakeMove(selectedMove);
                //console.log('移动执行完成，新状态:', newState);

                if (newState) {
                  //console.log('AI移动成功，重置思考状态');
                  // 清除消息
                  setTimeout(() => {
                    currentSetGameMessage('');
                  }, 1500);
                  return { ...newState, isAIThinking: false };
                }
              } else {
                //console.log('AI没有找到可用移动');
                currentSetGameMessage('AI无法移动');
                setTimeout(() => {
                  currentSetGameMessage('');
                }, 1500);
                return { ...prevState, isAIThinking: false };
              }
            } catch (error) {
              console.error('AI执行移动时出错:', error);
              currentSetGameMessage('AI执行出错');
              setTimeout(() => {
                currentSetGameMessage('');
              }, 1500);
              return { ...prevState, isAIThinking: false };
            }
          });
        }, 100); // 短暂延迟确保状态更新完成
      }, 500);

      return () => {
        //console.log('清理AI定时器');
        clearTimeout(timer);
      };
    }
  }, [gameState.currentPlayer, gameMode, gameState.status]); // 移除isAIThinking依赖避免循环

  // 胜利特效监听
  useEffect(() => {
    if (gameState.status === 'finished' && gameState.winner && gameState.winner !== lastWinner) {
      setLastWinner(gameState.winner);
      setWinRecord(prev => ({
        ...prev,
        [gameState.winner!]: prev[gameState.winner!] + 1
      }));
      setShowVictoryEffect(true);

      // 播放胜利音效
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // 创建胜利和弦音效（C-E-G和弦）
        oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
        oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
        oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5

        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.8);
      } catch (error) {
        console.log('Web Audio API not available, skipping victory sound');
      }
    }
  }, [gameState.status, gameState.winner, lastWinner]);

  // 更新悔棋按钮状态
  useEffect(() => {
    setCanUndo(gameState.gameHistory.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking);
  }, [gameState.gameHistory.length, gameState.status, gameState.isAIThinking]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">⚔️</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-gray-600 via-stone-600 to-slate-700 bg-clip-text text-transparent sm:mb-1">
                维京板棋
              </h1>
              <span className="text-2xl">🛡️</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            <button onClick={onBackToMenu} className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium" title="回到首页">
              <span>🏠</span>
              <span className="hidden sm:inline">首页</span>
            </button>
            {/* 返回按钮 */}
            <button onClick={onBackToModeSelect || onBackToMenu} className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium" title="返回模式选择">
              <span>🔙</span>
              <span className="hidden sm:inline">返回</span>
            </button>

            {/* 重新开始按钮 */}
            <button onClick={resetGame} className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium" title="重新开始">
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button onClick={undoMove} disabled={!canUndo} className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
              canUndo ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`} title="悔棋">
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button onClick={() => setShowRules(true)} className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium" title="游戏规则">
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="hnefatafl" />

            {/* 放大按钮 */}
            <button onClick={() => setIsBoardEnlarged(!isBoardEnlarged)} className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
              isBoardEnlarged ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-green-500 text-white hover:bg-green-600'
            }`} title={isBoardEnlarged ? "恢复正常布局" : "放大棋盘"}>
              <span>🔍</span>
              <span className="hidden sm:inline">{isBoardEnlarged ? '恢复' : '放大'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前玩家信息 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            <span className="text-sm sm:text-base font-medium">
              {gameState.status === 'playing' ? (
                <>
                  {gameState.currentPlayer === 'attacker' ? '🔴 进攻方' : '🔵 防守方'}回合
                  {gameState.isAIThinking && gameMode === 'pvc' && gameState.currentPlayer === 'attacker' && (
                    <span className="text-xs text-blue-600 animate-pulse ml-2">AI思考中...</span>
                  )}
                </>
              ) : (
                <span className="text-green-600">
                  {gameState.winner ? (
                    <span>{gameState.winner === 'defender' ? '🛡️ 防守方' : '⚔️ 进攻方'}获胜！</span>
                  ) : '游戏结束'}
                </span>
              )}
            </span>
            {/* 游戏消息 */}
            {gameMessage && (
              <span className="text-center text-yellow-800 text-sm font-medium">
                {gameMessage}
              </span>
            )}
          </div>

          {/* 游戏统计信息 */}
          <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
            <span>🔴 {gameState.capturedPieces.defender.length}</span>
            <span>🔵 {gameState.capturedPieces.attacker.length}</span>
            <span>步数: {gameState.totalMoves}</span>
          </div>
        </div>

        {/* 简化的战绩信息 */}
        {(winRecord.attacker > 0 || winRecord.defender > 0) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex justify-center space-x-4 text-xs text-gray-600">
              <span>进攻方胜场: {winRecord.attacker}</span>
              <span>防守方胜场: {winRecord.defender}</span>
              {winRecord.attacker + winRecord.defender > 0 && (
                <span>防守方胜率: {((winRecord.defender / (winRecord.attacker + winRecord.defender)) * 100).toFixed(0)}%</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <HnefataflBoard
            gameState={gameState}
            onCellClick={handleCellClick}
            disabled={gameState.status === 'finished' || gameState.isAIThinking}
            gameMode={gameMode}
            enlarged={isBoardEnlarged}
          />
        </div>
      </div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-amber-50 border-t border-amber-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-amber-800 text-center leading-relaxed">
          <strong>维京板棋规则：</strong>11×11棋盘{isBoardEnlarged && <span className="ml-3 text-orange-600">🔍 老人模式</span>}，进攻方(红)24枚 vs 防守方(蓝)13枚(含国王) |
          进攻方围捕国王获胜，防守方国王逃到角落获胜 | 棋子可直线移动任意格，夹吃对手棋子
        </p>
      </div>

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>

            <div className="text-center mb-6 pr-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">维京板棋游戏规则</h2>
              <div className="text-6xl mb-4">🛡️</div>
            </div>

            <div className="space-y-4 text-gray-700">
              {/* 游戏简介 */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-blue-800">📖 游戏简介</h3>
                <p className="text-sm leading-relaxed">
                  维京板棋（Hnefatafl）是古老的北欧不对称战略棋类游戏，源自维京时代。
                  游戏模拟防守方护卫国王逃离包围，进攻方围捕国王的对决场景。
                  这是不对称游戏的典型代表，双方拥有不同的棋子数量和获胜条件。
                </p>
              </div>

              {/* 游戏设置 */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-green-800">🎯 游戏设置</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>棋盘：</strong>11×11 方格棋盘</li>
                  <li><strong>防守方（蓝）：</strong>13枚棋子：1个国王 👑 + 12个卫士 🛡️</li>
                  <li><strong>进攻方（红）：</strong>24枚棋子：24个攻击者 ⚔️</li>
                  <li><strong>王座：</strong>棋盘中心位置（特殊标记 🏰）</li>
                  <li><strong>逃脱点：</strong>四个角落（金色圆圈标记）</li>
                </ul>
              </div>

              {/* 初始布局 */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-yellow-800">♟️ 初始布局</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>国王：</strong>放置在棋盘中心王座位置</li>
                  <li><strong>卫士：</strong>围绕国王形成十字形防守阵型</li>
                  <li><strong>攻击者：</strong>分布在棋盘四边，形成包围态势</li>
                </ul>
              </div>

              {/* 移动规则 */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-purple-800">🚶 移动规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>移动方向：</strong>所有棋子只能横向或纵向直线移动</li>
                  <li><strong>移动距离：</strong>可以移动任意格数，类似国际象棋的车</li>
                  <li><strong>移动限制：</strong>不能跳过其他棋子，不能斜向移动</li>
                  <li><strong>王座规则：</strong>只有国王可以进入或停留在王座（中心点），其他棋子不可走入</li>
                  <li><strong>角落规则：</strong>只有国王可以走到角落，其他棋子不可走入角落</li>
                  <li><strong>国王特权：</strong>国王离开中心点后还可以重新走回中心点</li>
                </ul>
              </div>

              {/* 夹吃规则 */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-red-800">⚔️ 夹吃规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>普通夹吃：</strong>当敌方棋子被己方两个棋子在直线上夹住时，该敌方棋子被吃掉</li>
                  <li><strong>夹吃举例：</strong>己方棋子 - 敌方棋子 - 己方棋子 → 中间敌方棋子被吃</li>
                  <li><strong>重要规则：</strong>棋盘的角落永远相当于走棋一方的虚拟棋子，可用来夹吃任何敌方棋子</li>
                  <li><strong>夹吃举例：</strong>己方棋子 - 敌方棋子 - 角落 → 敌方棋子被吃</li>
                  <li><strong>王座规则：</strong>空王座也可以作为夹吃的支撑点</li>
                </ul>
              </div>

              {/* 国王特殊规则 */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-amber-800">👑 国王特殊规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>国王捕获：</strong>需要四面包围才能捕获国王（上下左右都有攻击者）</li>
                  <li><strong>国王移动：</strong>国王的移动规则与其他棋子相同</li>
                  <li><strong>王座特权：</strong>国王可以在王座上停留，其他棋子不能</li>
                </ul>
              </div>

              {/* 获胜条件 */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-indigo-800">🏆 获胜条件</h3>
                <div className="text-sm space-y-2">
                  <div>
                    <strong className="text-blue-600">防守方胜利条件：</strong>
                    <ul className="ml-4 mt-1 space-y-1">
                      <li>✅ 国王成功到达任意一个角落逃脱点</li>
                      <li>✅ 吃掉所有攻击者（使其无法移动）</li>
                    </ul>
                  </div>
                  <div>
                    <strong className="text-red-600">攻击方胜利条件：</strong>
                    <ul className="ml-4 mt-1 space-y-1">
                      <li>✅ 四面包围并捕获国王</li>
                      <li>✅ 国王无法移动到任何角落</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 策略提示 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-gray-800">💡 策略提示</h3>
                <div className="text-sm space-y-2">
                  <p><strong className="text-blue-600">防守方策略：</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• 保护国王同时寻找通往角落的安全路径</li>
                    <li>• 利用卫士形成防守阵线，阻挡攻击者</li>
                    <li>• 适时牺牲卫士为国王开辟道路</li>
                  </ul>
                  <p><strong className="text-red-600">攻击方策略：</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• 快速向国王中心靠拢，形成包围圈</li>
                    <li>• 协调多个攻击者同时逼近国王</li>
                    <li>• 利用夹吃规则减少卫士数量</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowRules(false)}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 胜利特效 */}
      {showVictoryEffect && lastWinner && (
        <VictoryEffect
          winner={lastWinner === 'attacker' ? 'black' : 'white'}
          isVisible={showVictoryEffect}
          onHide={() => {
            setShowVictoryEffect(false);
            // 隐藏特效后自动重新开始游戏
            resetGame();
          }}
        />
      )}
    </div>
  );
};

export default HnefataflGame;